import { useCallback, useMemo, useSyncExternalStore } from 'react'
import type { MetroMapState } from './metroMapStore'
import type { Mode } from '../schema/types'
import { useMetroMapDispatch, useMetroMapStore } from './useMetroMapStore'
import { useSchemaOptional } from '../components/useSchema'
import { nightElementIds } from './nightMembership'

/**
 * Selector: is the user in Night Tube mode?
 *
 * Mirrors iOS `MetroMap.State.isNightMode: Bool` semantics — derived from
 * the existing tri-state `currentMode` slice (Web has not been renamed to a
 * boolean per cross-platform lead decision: iOS uses `Bool`, Web/Android
 * keep the tri-state slice and expose this selector for analytics + UI).
 */
export function selectIsNightMode(state: MetroMapState): boolean {
  return state.currentMode === 'night'
}

/**
 * React hook returning the live `isNightMode` boolean. Subscribes via
 * `useSyncExternalStore` so consumers re-render on mode flip.
 */
export function useIsNightMode(): boolean {
  const store = useMetroMapStore()
  return useSyncExternalStore(store.subscribe, () => selectIsNightMode(store.getState()))
}

/**
 * Three dispatch helpers mirroring Android task #3 API surface
 * (`toggleNightMode` / `enableNightMode` / `disableNightMode`). Returns
 * the current mode prior to the dispatch so analytics callers can
 * include `current_mode` / `target_mode` without a second store read.
 */
export interface NightModeActions {
  toggleNightMode: () => { prev: Mode; next: Mode }
  enableNightMode: () => { prev: Mode; next: Mode }
  disableNightMode: () => { prev: Mode; next: Mode }
}

export function useNightModeActions(): NightModeActions {
  const store = useMetroMapStore()
  const dispatch = useMetroMapDispatch()
  const schema = useSchemaOptional()
  // TASK-412 Q: the night-service id set fed to SetMode so the reducer can
  // clear non-night From/To/selected on night-enter. Null until the schema
  // loads (then the reducer simply skips clearing — harmless cold-start
  // edge). Includes connection/transition/additional ids too, but the
  // reducer only checks station ids against it, so the extra ids are inert.
  const nightStationIds = useMemo(
    () => (schema === null ? undefined : nightElementIds(schema)),
    [schema],
  )

  // Whether the open line view's line keeps any station in night mode — the
  // reducer closes a line view with nothing left to draw. Undefined when no
  // line is open or the schema is not loaded (the reducer then leaves it).
  const lineHasNightService = useCallback((): boolean | undefined => {
    const line = store.getState().selectedLine
    if (line === null || schema === null || nightStationIds === undefined) return undefined
    return schema.stations.some((s) => s.line.id === line.lineId && nightStationIds.has(s.id))
  }, [store, schema, nightStationIds])

  const toggleNightMode = useCallback(() => {
    const prev = store.getState().currentMode
    const next: Mode = prev === 'night' ? 'normal' : 'night'
    dispatch({
      kind: 'SetMode',
      mode: next,
      nightStationIds,
      lineHasNightService: lineHasNightService(),
    })
    return { prev, next }
  }, [store, dispatch, nightStationIds, lineHasNightService])

  const enableNightMode = useCallback(() => {
    const prev = store.getState().currentMode
    dispatch({
      kind: 'SetMode',
      mode: 'night',
      nightStationIds,
      lineHasNightService: lineHasNightService(),
    })
    return { prev, next: 'night' as Mode }
  }, [store, dispatch, nightStationIds, lineHasNightService])

  const disableNightMode = useCallback(() => {
    const prev = store.getState().currentMode
    dispatch({ kind: 'SetMode', mode: 'normal' })
    return { prev, next: 'normal' as Mode }
  }, [store, dispatch])

  return { toggleNightMode, enableNightMode, disableNightMode }
}
